import { FC, useId, ReactNode } from 'react'
import { twMerge } from '@lib/tailwind-merge'
import { Tooltip } from '@lib/tooltip'

export interface Props {
  id?: string
  checked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  className?: string
  tooltip?: ReactNode
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, 'id'>
}

export const Switch: FC<Props> = ({
  id,
  checked = false,
  onChange,
  disabled = false,
  className: classNameOverride,
  tooltip,
  tooltipProps,
}) => {
  const generatedId = useId()
  const switchId = id ?? generatedId

  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === ' ' || event.key === 'Enter') && !disabled) {
      event.preventDefault()
      if (onChange) {
        onChange(!checked)
      }
    }
  }

  const className = twMerge(
    // Base styles
    'relative inline-flex h-6 w-[39px] items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 shrink-0',
    // Background colors
    checked ? 'bg-success-500' : 'bg-neutral-300',
    // Disabled state
    disabled && 'opacity-50 cursor-not-allowed',
    // Active state
    !disabled && 'cursor-pointer',
    classNameOverride,
  )

  const thumbClassName = twMerge(
    // Base thumb styles
    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
    // Position based on checked state
    checked ? 'translate-x-[82%]' : 'translate-x-[2px]',
  )

  const tooltipId = `switch-tooltip-${switchId}`

  return (
    <>
      <button
        id={switchId}
        role="switch"
        type="button"
        disabled={disabled}
        className={className}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-tooltip-id={tooltipId}
      >
        <span className={thumbClassName} />
      </button>
      {tooltip && (
        <Tooltip id={tooltipId} {...tooltipProps}>
          {tooltip}
        </Tooltip>
      )}
    </>
  )
}
